import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import clone from 'ramda/src/clone';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';
import toLower from 'ramda/src/toLower';
import without from 'ramda/src/without';
import classNames from 'classnames';
import AdvancedSearch from '@components/@core/AdvancedSearch';
import Icon from '@components/@core/Icon';
import Toggle from '@components/@core/Toggle';
import AppActions from '~/actions/app';

const LibraryStatus = ({ className = '', type = 'Tracks', counts = 0, showCounts = true, showToggle = false, tracking }) => {
    const dispatch = useDispatch();

    const appRole = useSelector((state) => state.app.app_role_id);
    const authors = useSelector((state) => state.app.authors);
    const categories = useSelector((state) => state.app.categories);
    const departments = useSelector((state) => state.app.departments);
    const competencies = useSelector((state) => state.app.competencies);
    const selected = useSelector((state) => state.app.selectedTags);
    const published = useSelector((state) => state.app.published);
    const admin = appRole < 3;

    const handleDeselect = (target, id) => () => {
        const _selected = clone(selected);
        _selected[target] = without([id], _selected[target]);
        dispatch(AppActions.advancedsearchRequest(_selected, toLower(type), published));
    };

    const handleToggle = (checked) => {
        dispatch(AppActions.advancedsearchRequest(selected, toLower(type), !checked, tracking));
    };

    return (
        <div className={classNames('library-status', className)}>
            <div className="advanced-search">
                <AdvancedSearch mode={toLower(type)} published={Boolean(published)} tracking={tracking} />
            </div>
            {showCounts && (
                <p className="dsl-b16 text-500 text-capitalize mb-1">
                    {type}: {counts}
                </p>
            )}
            <div className="d-flex ssm-column jc-between ai-center">
                <div className="filters">
                    {selected.authors.length === 0 && selected.categories.length === 0 && selected.departments.length === 0 && selected.competencies.length === 0 ? (
                        <div className="d-flex chip" key="ls-all">
                            <span className="dsl-b14">Showing All</span>
                            <Icon name="fas fa-xmark cursor-pointer ms-1" color="#343f4b" size={14} />
                        </div>
                    ) : (
                        <>
                            {selected.authors.map((item, index) => (
                                <div className="d-flex chip" key={`ls-${index}`} onClick={handleDeselect('authors', item)}>
                                    <span className="dsl-b14">{find(propEq('id', item), authors).name}</span>
                                    <Icon name="fas fa-xmark cursor-pointer ms-1" color="#343f4b" size={14} />
                                </div>
                            ))}
                            {selected.categories.map((item, index) => (
                                <div className="d-flex chip" key={`ls-${index}`} onClick={handleDeselect('categories', item)}>
                                    <span className="dsl-b14">{find(propEq('id', item), categories).name}</span>
                                    <Icon name="fas fa-xmark cursor-pointer ms-1" color="#343f4b" size={14} />
                                </div>
                            ))}
                            {selected.departments.map((item, index) => (
                                <div className="d-flex chip" key={`ls-${index}`} onClick={handleDeselect('departments', item)}>
                                    <span className="dsl-b14">{find(propEq('id', item), departments).name}</span>
                                    <Icon name="fas fa-xmark cursor-pointer ms-1" color="#343f4b" size={14} />
                                </div>
                            ))}
                            {selected.competencies.map((item, index) => (
                                <div className="d-flex chip" key={`ls-${index}`} onClick={handleDeselect('competencies', item)}>
                                    <span className="dsl-b14">{find(propEq('id', item), competencies).name}</span>
                                    <Icon name="fas fa-xmark cursor-pointer ms-1" color="#343f4b" size={14} />
                                </div>
                            ))}
                        </>
                    )}
                </div>
                {admin && showToggle && (
                    <div className="toggle-section">
                        <Toggle className="mt-3 pb-2 mt-md-0 pb-md-0" reversed checked={!published} leftLabel="Published" rightLabel="Drafts" onChange={handleToggle} />
                    </div>
                )}
            </div>
        </div>
    );
};

LibraryStatus.propTypes = {
    className: PropTypes.string,
    type: PropTypes.string,
    counts: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showCounts: PropTypes.bool,
    showToggle: PropTypes.bool,
};

export default LibraryStatus;
